import { getApiClient } from "@src/api";
import {
	navbarBranchQueryKeys,
	navbarTreeQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { whiteboardQueryKeys } from "@src/components/board/queries/query-keys";
import { graphql } from "@src/gql/gql";
import { NavbarSection } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const transferWhiteboardMutation = graphql(/* GraphQL */ `
	mutation transferWhiteboard($whiteboardId: String!, $newOwnerEmail: String!) {
		whiteboards {
			transferOwnership(whiteboardId: $whiteboardId, newOwnerEmail: $newOwnerEmail) {
				name
				ownerId
				status
				permissions {
					publishedLink {
						isReadonly
						key
					}
					userPermissions {
						key
						value
					}
				}
			}
		}
	}
`);

/**
 * Transfer ownership mutation function.
 *
 * @param whiteboardId The ID of the whiteboard to transfer ownership of
 * @param newOwnerEmail The email of the new owner
 * @returns The mutation result data
 */
export const transferWhiteboardMutationFn = async ({
	whiteboardId,
	newOwnerEmail,
}: {
	whiteboardId: string;
	newOwnerEmail: string;
}) => {
	const client = getApiClient();

	const result = await client.mutate({
		mutation: transferWhiteboardMutation,
		variables: {
			whiteboardId,
			newOwnerEmail,
		},
	});

	if (!result.data?.whiteboards?.transferOwnership) {
		throw new Error("Failed to transfer whiteboard ownership");
	}

	return result.data.whiteboards.transferOwnership;
};
/**
 * Transfer ownership of a whiteboard to another user.
 *
 * @returns React Query mutation object
 */
export const useTransferWhiteboard = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: transferWhiteboardMutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: whiteboardQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
			queryClient.invalidateQueries({
				queryKey: navbarTreeQueryKeys.tree(NavbarSection.MyWhiteboards),
			});
		},
	});
};
